import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { ScheduledTypes } from '../../../../../../../api/scheduled-email.dao';
import { IEventMeetingsDTO, IGetMeetingsRequestParams, IMeetingDetailsDTO, IMeetingsDAO } from '../../../../../../../api/meetings.dao';
import { IGlobalStorage } from '../../../../../../../infrastructure/global-storage';
import { PagingViewModel } from '../../../../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { SortOrder } from '../../../../../../../api/shared.dto';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { TGetMeetingsSortKeys } from '../../../../../../../api/control.dao';
import { AttendeeRole } from '../../../../../../../api/attendees.dao';
import { IGNORE_ATTENDEES } from '../../../../../../../api/organizations.dao';
import { IEventDetailsDTO } from '../../../../../../../api/events.dao';
import { IDateTimeFormatter } from '../../../../../../../infrastructure/date-formatter';

const PAGE_SIZE = 20;

class ScheduledMeetingEmailPopupViewmodel {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    private static defaultRoles = [AttendeeRole.Participant, AttendeeRole.Registrant];

    private pagingViewModel: PagingViewModel;
    @mobx.observable
    private selectedMeetingId: number;
    @mobx.observable
    private selectedEmailId: string;
    @mobx.observable
    private selectedMeetingDetails: IMeetingDetailsDTO;
    @mobx.observable
    private selectedRoles: AttendeeRole[] = ScheduledMeetingEmailPopupViewmodel.defaultRoles;
    @mobx.observable
    private isLoading: boolean = false;
    @mobx.observable
    private isPopupOpen: boolean = false;
    @mobx.observable
    private meetingList: IMeetingDetailsDTO[] = [];
    @mobx.observable
    private sortOrderValue: SortOrder;
    @mobx.observable
    private sortFieldValue: TGetMeetingsSortKeys;

    constructor(
        public closeCallback: (id?: string, type?: ScheduledTypes) => void,
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly datetimeFormatter: IDateTimeFormatter = IoC.Container.get(IDateTimeFormatter),
    ) {
        this.initialize();
    }

    public get loading(): boolean {
        return this.isLoading;
    }

    public get isOpen(): boolean {
        return this.isPopupOpen;
    }

    public get meetingId(): number {
        return this.selectedMeetingId;
    }

    public get emailId(): string {
        return this.selectedEmailId;
    }

    public get roles(): AttendeeRole[] {
        return this.selectedRoles;
    }

    public get pagingVM(): PagingViewModel {
        return this.pagingViewModel;
    }

    public get meetings(): IMeetingDetailsDTO[] {
        return this.meetingList;
    }

    public get sortOrder(): SortOrder {
        return this.sortOrderValue;
    }

    public get sortField(): TGetMeetingsSortKeys {
        return this.sortFieldValue;
    }

    public get isApplyButtonDisabled(): boolean {
        return this.loading || !this.meetingId || !this.roles.length;
    }

    public get isEditMode(): boolean {
        return !!this.selectedEmailId;
    }

    public get event(): IEventDetailsDTO {
        return this.globalStorage.getEvent();
    }

    public get dateTimeFormatter(): IDateTimeFormatter {
        return this.datetimeFormatter;
    }

    public getMeetingById = (id: number = this.selectedMeetingId): IMeetingDetailsDTO | null => {
        const meeting = this.getMeetingFromCurrentList(id);
        if (meeting) return meeting;

        if (this.selectedMeetingDetails && this.selectedMeetingDetails.meetingId === id) {
            return this.selectedMeetingDetails;
        }

        return null;
    };

    public initialize = (): void => {
        this.sortFieldValue = 'startDateTime';
        this.sortOrderValue = SortOrder.Desc;
        this.initializePaging();
        this.fetchMeetingList();
    };

    @mobx.action
    public openPopUp = (): void => {
        this.isPopupOpen = true;
    };

    @mobx.action
    public openEditPopUp = async (emailId: string, meetingId: number, roles: AttendeeRole[]): Promise<void> => {
        this.selectedMeetingId = meetingId;
        this.selectedRoles = roles || ScheduledMeetingEmailPopupViewmodel.defaultRoles;
        this.selectedEmailId = emailId;
        this.isPopupOpen = true;

        if (!this.getMeetingFromCurrentList(meetingId)) {
            await this.fetchMeetingDetailsById(meetingId);
        }
    };

    public getMeetingFromCurrentList(meetingId: number): IMeetingDetailsDTO {
        return this.meetingList.find(x => x.meetingId === meetingId);
    };

    @mobx.action
    public closePopUp = (): void => {
        this.isPopupOpen = false;
        this.cleanup();
    };

    @mobx.action
    public setMeetingId = (meetingId: number): void => {
        this.selectedMeetingId = meetingId;
    };

    @mobx.action
    public checkRole = (role: AttendeeRole): void => {
        this.selectedRoles.push(role);
    };

    @mobx.action
    public uncheckRole = (role: AttendeeRole): void => {
        this.selectedRoles = this.selectedRoles.filter(x => x !== role);
    };

    @mobx.action
    public setSortParams = (sortField: TGetMeetingsSortKeys, sortOrder: SortOrder): void => {
        this.sortFieldValue = sortField;
        this.sortOrderValue = sortOrder;
        this.fetchMeetingList();
    };

    @mobx.action
    public fetchMeetingList = async (): Promise<void> => {
        try {
            this.isLoading = true;

            const params = {
                eventId: this.globalStorage.getEvent().eventId,
                accountId: this.globalStorage.getEvent().accountId,
                withoutRoom: false,
                size: this.pagingViewModel.getPageSize(),
                page: this.pagingViewModel.getCurrentPageForBE(),
                sortOrder: this.sortOrderValue,
                sortField: this.sortFieldValue,
                ignoreRegistrants: IGNORE_ATTENDEES,
            };
            const response = await this.meetingsDAO.getEventMeetings(params as IGetMeetingsRequestParams);
            this.onSuccess(response);
        } catch (e) {
            ScheduledMeetingEmailPopupViewmodel.handleApiException(e);
        } finally {
            this.isLoading = false;
        }
    };

    public handleSubmit = (): void => {
        this.closeCallback(this.emailId, ScheduledTypes.Meeting);
        this.closePopUp();
    };

    @mobx.action
    private onSuccess = (response: IEventMeetingsDTO): void => {
        this.meetingList = response.content;
        this.isLoading = false;
        this.pagingVM.setTotalRecords(response.totalElements);
    };

    private initializePaging = (): void => {
        this.pagingViewModel = new PagingViewModel({ pageSize: PAGE_SIZE });
        mobx.reaction(this.pagingViewModel.getCurrentPage, this.fetchMeetingList);
        this.pagingViewModel.setPageSize(PAGE_SIZE);
    };

    @mobx.action
    private fetchMeetingDetailsById = async (meetingId: number): Promise<void> => {
        try {
            const params = {
                eventId: this.globalStorage.getEvent().eventId,
                accountId: this.globalStorage.getEvent().accountId,
                meetingId: meetingId,
            };
            const meetingDetails = await this.meetingsDAO.getMeetingDetails(params);

            mobx.runInAction(() => {
                this.selectedMeetingDetails = meetingDetails;
            });
        } catch (e) {
            ScheduledMeetingEmailPopupViewmodel.handleApiException(e, 'Failed to load meeting details');
        }
    };

    private cleanup = (): void => {
        this.isLoading = false;
        this.selectedMeetingId = null;
        this.selectedEmailId = null;
        this.selectedRoles = [AttendeeRole.Participant, AttendeeRole.Registrant];
    };
}

export { ScheduledMeetingEmailPopupViewmodel };